/**
 * Shared Set list fetch powered by TanStack Query.
 * Multiple SetSelector instances (e.g. in PolicyRule edit dialog) share a single
 * cached query per mode instead of each triggering their own GET entities/Set.
 */

import { useQuery } from '@tanstack/react-query';
import { entityService } from '../services/entityServiceSingleton';

const PAGE_SIZE = 100;

async function fetchAllSets(showOnlyValidTargets: boolean): Promise<any[]> {
    let allSets: any[] = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
        const response = await entityService.getEntitiesByType('Set', currentPage, PAGE_SIZE);
        const pageSets = response.data || [];
        allSets = [...allSets, ...pageSets];
        const totalResults = response.totalResults || 0;
        const totalPages = Math.ceil(totalResults / PAGE_SIZE);
        hasMore = currentPage < totalPages;
        currentPage++;
    }

    if (showOnlyValidTargets) {
        return allSets.filter((set: any) => {
            const hasFilterScope =
                set.FilterScope &&
                ((typeof set.FilterScope === 'string' && set.FilterScope.trim() !== '') ||
                    (typeof set.FilterScope === 'object' &&
                        (set.FilterScope.Id || set.FilterScope.DisplayName)));
            return hasFilterScope;
        });
    }

    return allSets;
}

export interface UseSetListResult {
    sets: any[];
    loading: boolean;
    error: string | null;
}

/**
 * Returns a shared, cached list of Set entities via TanStack Query.
 * Deduplicates requests so multiple SetSelectors in the same tree
 * (e.g. PolicyRule TargetSet, RequestorSet, TargetPostOpSet)
 * share one cached query per showOnlyValidTargets mode.
 *
 * Cache is automatically invalidated when a Set is saved/deleted
 * (via query key ['entities', 'Set'] invalidation in save/delete mutations).
 */
export function useSetList(showOnlyValidTargets: boolean): UseSetListResult {
    const query = useQuery({
        queryKey: ['sets', showOnlyValidTargets ? 'valid' : 'all'] as const,
        queryFn: () => fetchAllSets(showOnlyValidTargets),
        staleTime: 5 * 60_000,
    });

    return {
        sets: query.data ?? [],
        loading: query.isLoading,
        error: query.error ? 'Failed to load sets' : null,
    };
}
